import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import { fetchTasksQueueAssignableUsers } from '../../../../redux/slices/tasksQueueSlice';

import { updateTaskToUser } from '../../../../services/tasksQueue.service';
import AuthService from '../../../../services/auth.service';

import { UserProfile } from '../../../../types/UserProfile';
import { ResourceType } from '../../../../types/ResourceType';
import { TasksReaderQueue } from '../../../../constants/Constants';

const useStyles = makeStyles(() => ({
  selectInput: {
    padding: 0,
    '& .MuiFormControl-root.MuiTextField-root': {
      height: 17,
      width: 240,
      border: 'none',
    },
    '& .MuiOutlinedInput-root': {
      height: 'auto',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&.Mui-focused': {
        height: 17,
        width: 240,
      },
      '& .MuiInput-root .MuiInput-input': {
        width: '200px',
        fontSize: '13.12px',
      },
    },
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&.Mui-focused': {
        height: 'auto',
      },
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      textAlign: 'left',
    },
    '& .MuiInput-root .MuiInput-input': {
      width: '200px',
      height: '12px',
      fontSize: '13.12px',
    },
    '& .MuiAutocomplete-option': {
      fontSize: '13.12px',
    },
  },
}));

interface AssignableUser {
  value: string;
  resourceType: ResourceType;
  id: string;
  label: string;
}

const TasksQueueOperator = ({ taskId, assignedUserName = '', handleStatusChanged }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const tasksQueueState = useAppSelector((state) => state.tasksQueue);

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [selectedUserName, setSelectedUserName] = useState(assignedUserName);
  const [options, setOptions] = useState<any[]>([]);

  useEffect(() => {
    AuthService.getUserProfile().then(setUserProfile);
  }, []);

  useEffect(() => {
    if (tasksQueueState.assignableUsers.length === 0) {
      dispatch(fetchTasksQueueAssignableUsers());
    }

    if (tasksQueueState.assignableUsers.length > 0) {
      setOptions([
        {
          resourceType: ResourceType.AppUser,
          id: userProfile?.UserId || '',
          label: TasksReaderQueue.AssignToMe,
        },
        ...tasksQueueState.assignableUsers,
      ]);
    }
  }, [tasksQueueState.assignableUsers]);

  useEffect(() => {
    setSelectedUserName(assignedUserName);
  }, [assignedUserName]);

  return (
    <Autocomplete
      options={options?.map((p: AssignableUser) => {
        return { id: p.id, label: p.label };
      })}
      className={classes.autocomplete}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      value={selectedUserName ? ({ label: selectedUserName } as AssignableUser) : null}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder='Assign to...'
          variant='standard'
          InputLabelProps={{ shrink: false }}
          InputProps={{
            ...params.InputProps,
            className: classes.selectInput,
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.label}
        </li>
      )}
      noOptionsText={<div>No options</div>}
      getOptionLabel={(option) => option.label}
      onChange={async (event: React.SyntheticEvent, value: any | null) => {
        let userId: string | null = null;

        if (value && value.label === TasksReaderQueue.AssignToMe) {
          userId = userProfile?.UserId || null;
        } else if (value) {
          userId = value.id || null;
        }

        const res = await updateTaskToUser(taskId, { userId });
        const assignedUser = res.data.assignedUserName;

        if (assignedUser === value?.label || assignedUser === userProfile?.given_name || (!value && !assignedUser)) {
          setSelectedUserName(assignedUser);
          handleStatusChanged();
        }
      }}
    />
  );
};

export default TasksQueueOperator;
